import React from 'react';
import { object, number, array, bool, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { I18n, I18nProvider } from '@fiverr-private/fiverr_context';
import { PACKAGE_POSITIONS } from '../../types/packagePositions';
import CheckoutFormHOC from '../shared/CheckoutFormHOC';
import SafePureComponent from '../shared/SafePureComponent';
import { shouldShowSelectPackage } from '../../utils/shouldShowSelectPackage';
import { enrichFeaturesRowData, preparePricingFactors } from '../../utils/pricingFactors';
import { calculateServiceName, enrichServices, groupFeaturesByService } from '../../utils/studios';
import { flattenCalculators } from '../../utils/calculator';
import { isRecurringGig } from '../../reducers/packages';
import { shouldDisplayFinalPriceUpfront } from '../../utils/experiments/finalPriceUpfront';
import PackageType from './PackageType';
import Description from './Description';
import Revisions from './Revisions';
import DeliveryTime from './DeliveryTime';
import CalculatorRow from './CalculatorRow';
import SelectPackage from './SelectPackage';
import PricingFactorRow from './PricingFactorRow';
import ServiceNameRow from './ServiceNameRow';
import RecurringOptionsRow from './RecurringOptionsRow';

import './style.scss';

const renderServiceCalculator = (calculators) => {
    if (calculators) {
        return <CalculatorRow calculatorRowData={calculators}/>;
    }
};

const renderServiceFeatures = (features) => {
    if (features) {
        return features.map((feature) => <PricingFactorRow key={feature.id} {...feature}/>);
    }
};

const renderServices = (packageList, services) => {
    const enrichedServices = enrichServices(services, packageList);
    const enrichedFeatures = enrichFeaturesRowData(packageList);
    const featuresByService = groupFeaturesByService(enrichedServices, enrichedFeatures);

    return (
        enrichedServices.map((({ id, subCategoryId, nestedSubCategoryId, calculators }) => (
            <I18nProvider key={id}>
                {({ i18n }) => [
                    <ServiceNameRow key={id} name={calculateServiceName(i18n, subCategoryId, nestedSubCategoryId)}/>,
                    renderServiceCalculator(calculators),
                    renderServiceFeatures(featuresByService[id])
                ]}
            </I18nProvider>
        )))
    );
};

const renderPricingFactorRows = (packageList) => {
    const pricingFactors = preparePricingFactors(packageList);

    return (
        pricingFactors.map(((pricingFactor) =>
            <PricingFactorRow key={pricingFactor.id} {...pricingFactor}/>))
    );
};

const renderSingleCalculatorRow = (packageList) => {
    const calculatorRowData = flattenCalculators(packageList);

    if (calculatorRowData.length !== packageList.length) {
        return;
    }

    return (
        <CalculatorRow calculatorRowData={calculatorRowData}/>
    );
};

const Table = (
    { packageList, services, isRecurringGig = false },
    { general }
) => {
    const { isStudio } = general;
    const showSelectPackage = shouldShowSelectPackage(general);
    const showServiceFeeIcon = shouldDisplayFinalPriceUpfront();

    return (
        <div className="gig-page-packages-table">
            <h2 className="section-title">
                <I18n k={'gig_page_perseus.packages_table.title'}/>
            </h2>
            <table>
                <colgroup>
                    <col/>
                    {packageList.map(({ id }) => <col key={id}/>)}
                </colgroup>
                <tbody>
                    <PackageType showServiceFeeIcon={showServiceFeeIcon}/>
                    <Description/>
                    {isStudio && <Revisions/>}
                    {isStudio && <DeliveryTime showSelection={false}/>}
                    {isStudio && renderServices(packageList, services)}
                    {!isStudio && renderPricingFactorRows(packageList)}
                    {!isStudio && <Revisions/>}
                    {!isStudio && <DeliveryTime/>}
                    {!isStudio && renderSingleCalculatorRow(packageList)}
                    {isRecurringGig && <RecurringOptionsRow/>}
                    {showSelectPackage && <SelectPackage showServiceFeeIcon={showServiceFeeIcon}/>}
                </tbody>
            </table>
        </div>
    );
};

Table.propTypes = {
    packageList: arrayOf(object),
    services: arrayOf(object),
    isRecurringGig: bool
};

Table.contextTypes = {
    general: object
};

const mapStateToTableProps = ({ packages }) => {
    const { packageList } = packages;

    return {
        packageList,
        isRecurringGig: isRecurringGig(packages)
    };
};

const TableConnect = connect(mapStateToTableProps)(Table);

const PackagesTable = ({ selectedPackageId, services }) => (
    <CheckoutFormHOC className="gig-page-packages-table-form"
        selectedPackageId={selectedPackageId}
        source={PACKAGE_POSITIONS.TABLE}>
        <TableConnect services={services}/>
    </CheckoutFormHOC>
);

PackagesTable.propTypes = {
    selectedPackageId: number,
    services: array
};

const mapStateToTableFormProps = ({ packages }) => ({
    selectedPackageId: packages.selectedPackageId
});
const PackagesTableWrapper = connect(mapStateToTableFormProps)(PackagesTable);

export { PackagesTableWrapper, Table };
export default SafePureComponent(PackagesTableWrapper);
